import { ChevronDown, Menu, Phone, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { LandingFooter } from "@/components/landing/Footer";
import { LandingForm } from "@/components/landing/LandingForm";
import { LandingMarquee } from "@/components/landing/LandingMarquee";
import { LandingReviews } from "@/components/landing/LandingReviews";
import { CookieConsent } from "@/components/landing/CookieConsent";
import { MobileBar } from "@/components/landing/MobileBar";
import { ScrollTop } from "@/components/landing/ScrollTop";
import { Arrow, ArrowRight, Check, Civic, Doc, Euro, Partner, Plus, Shield } from "@/components/landing/icons";
import { STAT_PRISE_EN_CHARGE } from "@/config/site";
import { SEGMENTS } from "@/pages/siteContent";
import "@/landing.css";

const SITE_LOGO = "/airthermo-logo-site.png";
const U = (id, q) => `https://images.unsplash.com/${id}?${q}`;
const FACES = ["photo-1758686254563-5c5ab338c8b9", "photo-1732395805034-e0bf859665e5", "photo-1774600166834-175d06febcd4"].map((id) => U(id, "w=120&h=120&fit=crop&q=70"));
const HOW_IMG = U("photo-1581092446327-9b52bd1570c2", "crop=entropy&cs=srgb&fm=jpg&q=80&w=1200");
const CIVIC_IMG = U("photo-1784386178176-0915380a1b8d", "w=400&q=70");
const FINAL_IMG = U("photo-1774600166834-175d06febcd4", "crop=entropy&cs=srgb&fm=jpg&q=80&w=1000");
const KPIS = [
  [Euro, "0 € de courtage", "Rémunéré par la prime CEE, jamais par vous"], [Shield, "Dossier conforme", "Exigences du Pôle National CEE"],
  [Partner, "Partenaires RGE", "Installateurs qualifiés"], [Doc, "BAR-TH-179 · BAT-TH-163", "Résidentiel et tertiaire"],
];
const STEPS = [
  ["Premier échange", "Nous étudions votre bâtiment, votre système actuel et votre éligibilité aux CEE.", "a"],
  ["Étude & montage", "Dimensionnement par l'installateur partenaire RGE, montage du dossier CEE par Airthermo.", "b"],
  ["Installation", "Pose de la PAC collective par l'installateur qualifié, suivi du chantier par votre conseiller.", "i"],
  ["Prime versée", `La prime CEE est déduite directement du devis et couvre aussi notre accompagnement : dans ${STAT_PRISE_EN_CHARGE}, vous n'avez rien à payer.`, "a"],
];
export const d = (ms) => ({ "--d": `${ms}ms` });
const WHO = { a: "Airthermo", i: "Installateur RGE", b: "Airthermo + installateur" };
export const Who = ({ who }) => <span className={`lp-who lp-who-${who}`} data-testid="step-who">{WHO[who]}</span>;

export function useReveal(dep) {
  useEffect(() => {
    const root = document.querySelector(".lp");
    const html = document.documentElement;
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) html.style.scrollBehavior = "smooth";
    requestAnimationFrame(() => root?.classList.add("lp-ready"));
    const io = new IntersectionObserver((entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add("lp-in"); io.unobserve(e.target); } }), { rootMargin: "0px 0px -60px 0px" });
    const watch = () => document.querySelectorAll(".lp .lp-rv:not(.lp-in)").forEach((el) => io.observe(el));
    watch();
    const mo = new MutationObserver(watch);
    mo.observe(root, { childList: true, subtree: true });
    return () => { io.disconnect(); mo.disconnect(); html.style.scrollBehavior = ""; };
  }, [dep]);
}

const NAV = [["#methode", "Méthode", "methode"], ["#avis", "Avis", "avis"], ["#faq", "FAQ", "faq"]];
const SegMenu = () => (
  <div className="lp-dropmenu" data-testid="site-nav-dropdown">
    {SEGMENTS.map((s, i) => <Link key={s.slug} to={`/pompe-a-chaleur/${s.slug}`} data-testid={`site-nav-seg-${s.slug}`}><span className={`lp-dropic lp-c${i % 4}`}><s.icon size={16} /></span><span><strong>{s.label}</strong><small>Fiche {s.fiche}</small></span></Link>)}
  </div>
);

export function LandingNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="lp-nav" onClick={(e) => e.target.closest("a") && setOpen(false)}>
      <Link to="/" className="lp-logo" data-testid="site-logo-link"><img src={SITE_LOGO} alt="Airthermo — Cabinet de courtage énergie" data-testid="site-logo" /></Link>
      <a href="tel:+33255997758" className="lp-navphone" data-testid="site-nav-phone"><span className="lp-navphone-ic"><Phone size={14} /></span><span><strong>02 55 99 77 58</strong><small>de 10h à 18h</small></span></a>
      <div className="lp-navend">
        <ul className="lp-navl">
          <li><Link to="/le-cabinet" data-testid="site-nav-cabinet">Le cabinet</Link></li>
          <li className="lp-drop"><a href="#pour-qui" data-testid="site-nav-pour-qui">Pour qui<ChevronDown size={14} /></a><SegMenu /></li>
          {NAV.map(([h, l, id]) => <li key={id}><a href={h} data-testid={`site-nav-${id}`}>{l}</a></li>)}
        </ul>
        <a href="#etude" className="lp-btn lp-sm" data-testid="site-nav-cta">Étude gratuite<span className="lp-ico"><Arrow /></span></a>
        <button type="button" className="lp-burger" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="site-nav-burger">{open ? <X size={18} /> : <Menu size={18} />}</button>
      </div>
      {open && (
        <div className="lp-mobmenu" data-testid="site-nav-links">
          <Link to="/le-cabinet" data-testid="site-mobnav-cabinet">Le cabinet</Link>
          <a href="#pour-qui">Pour qui</a><SegMenu />
          {NAV.map(([h, l, id]) => <a key={id} href={h}>{l}</a>)}
        </div>
      )}
    </header>
  );
}

export const LandingHero = ({ image, alt, eyebrow, title, lead, proofs, top, source, building, highlight }) => (
  <section className="lp-hero">
    <div className="lp-hero-bg"><img src={image} alt={alt} /></div>
    <div className="lp-wrap">
      <div>
        {top}
        <span className="lp-eyebrow lp-rv">{eyebrow}</span>
        <h1 className="lp-rv" style={d(60)} data-testid="hero-title">{title}</h1>
        {highlight}
        <p className="lp-lead lp-rv" style={d(120)}>{lead}</p>
        <ul className="lp-proofs lp-rv" style={d(180)}>{proofs.map((p) => <li key={p}><Check />{p}</li>)}</ul>
        <div className="lp-trust lp-rv" style={d(240)}>
          <div className="lp-faces">{FACES.map((src) => <img key={src} src={src} alt="" />)}</div>
          <p><strong>Un duo dédié</strong><br />votre conseiller Airthermo et votre installateur RGE.</p>
        </div>
      </div>
      <div className="lp-leadcard lp-rv" id="contact" style={d(160)}>
        <div className="lp-core" id="etude">
          <h3>Votre étude d'éligibilité gratuite</h3>
          <p className="lp-sub">Un conseiller vous rappelle pour évaluer votre projet et le montant de la prime CEE.</p>
          <LandingForm key={source} source={source} defaultBuilding={building} />
          <Labels testid="hero-labels" />
        </div>
      </div>
    </div>
    <LandingMarquee />
  </section>
);

const Labels = ({ testid }) => (
  <div className="lp-labels" data-testid={testid}>
    <img src="/logo-rge.png" alt="RGE — Reconnu Garant de l'Environnement" loading="lazy" />
    <img src="/logo-cee.png" alt="Les Certificats d'Économies d'Énergie" loading="lazy" />
  </div>
);

export const KpiBand = () => (
  <div className="lp-band">
    <div className="lp-wrap">
      {KPIS.map(([Icon, t, s], i) => <div key={t} className="lp-kpi lp-rv" style={d(i * 60)}><span className={`lp-k lp-c${i}`}><Icon /></span><div><strong>{t}</strong><small>{s}</small></div></div>)}
    </div>
  </div>
);

export const SegmentCards = ({ current, title }) => (
  <section id="pour-qui" className="lp-section">
    <div className="lp-wrap">
      <div className="lp-head">
        <div><span className="lp-eyebrow lp-rv">Pour qui</span><h2 className="lp-rv" style={d(60)}>{title}</h2></div>
        <p className="lp-rv" style={d(120)}>Une page dédiée par type de bâtiment : enjeux, fiche CEE applicable et réponses aux questions de chaque décideur.</p>
      </div>
      <div className="lp-segs">
        {SEGMENTS.map((s, i) => (
          <Link key={s.slug} to={`/pompe-a-chaleur/${s.slug}`} className={`lp-seg lp-bezel lp-rv${s.slug === current ? " lp-current" : ""}`} style={d((i % 3) * 60)} data-testid={`site-card-${s.slug}`}>
            <div className="lp-core">
              <div className="lp-ph"><img src={s.cardImage || s.image} alt={s.label} loading="lazy" /></div>
              <div className="lp-tx"><span className="lp-fiche">{s.fiche}</span><h3>{s.label}</h3><p>{s.cardText || s.card}</p><span className="lp-more">{s.slug === current ? "Vous êtes ici" : "Découvrir"}<ArrowRight /></span></div>
            </div>
          </Link>
        ))}
        <div className="lp-seg lp-bezel lp-rv" style={d(120)} data-testid="site-card-collectivites">
          <div className="lp-core">
            <div className="lp-ph"><img src={CIVIC_IMG} alt="Mairie" loading="lazy" /><span className="lp-phbadge"><Civic /></span></div>
            <div className="lp-tx"><span className="lp-fiche">Bâtiments publics</span><h3>Collectivités</h3><p>Mairies, écoles, équipements publics : un projet clé en main, largement financé par les CEE.</p></div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const Method = ({ image = HOW_IMG }) => (
  <section id="methode" className="lp-section lp-how">
    <div className="lp-wrap">
      <div>
        <span className="lp-eyebrow lp-rv">Notre méthode</span>
        <h2 className="lp-rv" style={d(60)}>Un duo dédié, du <em>diagnostic</em> à la prime</h2>
        <ol className="lp-steps">{STEPS.map(([t, x, who], i) => <li key={t} className="lp-step lp-rv" style={d(i * 60)}><span className={`lp-n lp-c${i}`}>{i + 1}</span><div><h3>{t}<Who who={who} /></h3><p>{x}</p></div></li>)}</ol>
      </div>
      <div className="lp-howvisual lp-rv" style={d(120)}>
        <div className="lp-bezel"><div className="lp-core"><img src={image} alt="Technicien et cliente étudiant les plans du bâtiment" loading="lazy" /></div></div>
        <div className="lp-cee" id="cee"><div className="lp-in-card">
          <span className="lp-t">Les fiches CEE que nous mobilisons</span>
          <div className="lp-row"><div><strong>BAR-TH-179</strong><small>Résidentiel collectif · PAC air/eau</small></div><div><strong>BAT-TH-163</strong><small>Tertiaire · PAC air/eau</small></div></div>
          <div className="lp-ok"><Shield />Meilleure valorisation, dossier sécurisé et conforme.</div>
        </div></div>
      </div>
    </div>
  </section>
);

export const ReviewsFaq = ({ faq, title }) => (
  <section className="lp-section lp-duo" id="avis">
    <div className="lp-wrap">
      <LandingReviews />
      <div id="faq">
        <span className="lp-eyebrow lp-rv">Questions fréquentes</span>
        <h2 className="lp-rv lp-faqtitle" style={d(60)}>{title}</h2>
        <div className="lp-faqgrid lp-rv" style={d(120)} data-testid="faq-list">
          {[0, 1].map((col) => (
            <div key={col}>
              {faq.filter((_, i) => i % 2 === col).map(([q, a], i) => <details key={q} data-testid="faq-item"><summary>{q}<span className="lp-pm"><Plus /></span></summary><p>{a}</p></details>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const FinalCta = ({ image = FINAL_IMG, source = "accueil" }) => (
  <>
    <section className="lp-final">
      <div className="lp-wrap">
        <div className="lp-box lp-rv">
          <div className="lp-txt">
            <span className="lp-eyebrow">Prêt à vous lancer ?</span>
            <h2>Votre bâtiment est peut-être <em>éligible</em>. Vérifions-le ensemble.</h2>
            <p>L'étude est gratuite et sans engagement : un conseiller vous rappelle et chiffre la prime CEE de votre projet.</p>
            <a href="#etude" className="lp-btn lp-light" data-testid="site-hero-cta">Demander mon étude gratuite – 0 € de courtage<span className="lp-ico"><Arrow /></span></a>
          </div>
          <div className="lp-fph"><img src={image} alt="Deux techniciennes en casque discutant d'un projet" loading="lazy" /></div>
        </div>
      </div>
    </section>
    <MobileBar source={source} />
    <ScrollTop />
    <CookieConsent />
    <LandingFooter logo={SITE_LOGO} labels={<Labels testid="footer-labels" />} />
  </>
);
